import IsotypeIcon from '@avanta/shared/assets/avanta-simple.svg';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { cn } from '@avanta/shared/utils/cn';
import { isPastDate as isPastDateUtils } from '@avanta/shared/utils/date';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { VideoDialog } from 'components/VideoDialog';
import { ROUTES } from 'constants/routes';
import { WELLBEING_CONFIG, WellbeingTypes } from 'constants/wellbeing';
import { useTagMap } from 'hooks/useWellbeingContent';
import { useComponentTracking } from 'tracking/hooks/useTrackingComponent';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { ResourceType } from 'types/tracking';
import { openInNewTab } from 'utils/common';
import { resolveTrackingCategory } from 'utils/tracking';
import { getMonthFromDate } from 'utils/wellbeing';
import { EnrollmentMonthTag } from './EnrollmentMonthTag';
import { cardStyles } from './styles';
import { type ContentCardProps } from './types';
import { getContentCardMetaRows, tagCategoryBadge } from './utils';

export const ContentCard = ({
  author,
  audioURL,
  description,
  durationInMinutes,
  imageURL,
  trainingLink,
  videoURL,
  link,
  content,
  showCategoryIcon = false,
  tags,
  title,
  startTime,
  type,
  id,
  isVertical,
}: ContentCardProps): React.ReactElement => {
  const { showDialog } = useDialogContext();
  const { trackResourceOpen } = useComponentTracking();
  const { trackTrainingSessionAttended } = useTrackingEvents();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const tagMap = useTagMap();
  const { icon: CardTypeIcon, color: categoryTypePalette } =
    WELLBEING_CONFIG[type ?? WellbeingTypes.trainings];
  const primaryTag = tags?.[0];
  const displayTag = tagMap?.get(primaryTag ?? '');
  const monthTag = startTime ? getMonthFromDate(startTime) : null;

  const isVideoTip = type === WellbeingTypes.tips && Boolean(videoURL);
  const isArticle = !!content?.length;
  const isPastDate = !!(startTime && isPastDateUtils(startTime));
  const isOpenForEnrollment = !!(trainingLink && startTime) && !isPastDate && !videoURL;
  const isButtonDisabled = isPastDate && !videoURL;

  const handleOnClick = async (): Promise<void> => {
    const category = resolveTrackingCategory(displayTag ?? primaryTag);
    if (isArticle && id) {
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.ARTICLE,
        category,
      });
      navigate(ROUTES.WELL_BEING_ARTICLE.replace(':id', id));
      return;
    }
    if (videoURL) {
      showDialog({
        component: <VideoDialog video={videoURL} title={title} isVertical={isVertical} />,
      });
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.TIPS,
        category,
      });
      return;
    }
    if (audioURL) {
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.PODCAST,
        category,
      });
      openInNewTab(audioURL);
      return;
    }
    if (trainingLink) {
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.TRAINING,
        category,
      });
      if (startTime && !isPastDate) {
        trackTrainingSessionAttended({
          name: title,
          date: startTime,
          category,
        });
      }
      openInNewTab(trainingLink);
      return;
    }
    if (link && !isArticle) {
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.EBOOK,
        category,
      });
      openInNewTab(link);
      return;
    }
  };

  const getButtonText = (): string => {
    if (isButtonDisabled) return t('ContentCard.states.finished');
    if (trainingLink) {
      return videoURL ? t('TrainingCard.watchRecording.cta') : t('ContentCard.inscription.cta');
    }
    if (audioURL) return t('ContentCard.listen.cta');
    if (videoURL) return t('ContentCard.watchRecording.cta');
    if (link || isArticle) return t('ContentCard.read.cta');
    return t('ContentCard.inscription.cta');
  };

  const showTrailingIcon = isOpenForEnrollment;
  const durationLabel = durationInMinutes ? `${durationInMinutes}min` : null;
  const metaRows = getContentCardMetaRows({ isVideoTip, startTime, durationLabel });

  return (
    <div
      className={cn(
        'bg-card text-card-foreground rounded-tr-3xl rounded-br-3xl rounded-bl-3xl rounded-tl-none border border-plum-200 overflow-hidden flex flex-col',
        cardStyles.minWidth,
        showCategoryIcon ? 'h-full am-3xl:min-h-96' : cn('h-full', cardStyles.minHeight)
      )}
    >
      <div className="h-40 w-full bg-gray-100 relative">
        {imageURL && (
          <div className="relative w-full h-full overflow-hidden">
            <img
              src={IsotypeIcon}
              alt={title}
              className="absolute top-5 -right-16 rotate-305 h-35 opacity-40"
            />
            <img src={imageURL} alt={title} className="w-full h-full object-cover" />
          </div>
        )}
        {monthTag && isOpenForEnrollment && (
          <EnrollmentMonthTag className="absolute top-0 left-0 z-20">{monthTag}</EnrollmentMonthTag>
        )}
        {showCategoryIcon && CardTypeIcon && (
          <div className="absolute left-2 -bottom-6 z-20">
            <div className={cn(cardStyles.categoryTypeBubble, categoryTypePalette)}>
              <CardTypeIcon
                className={cardStyles.categoryTypeGlyph}
                strokeWidth={1.5}
                aria-hidden
              />
            </div>
          </div>
        )}
      </div>
      <div className={cn('flex min-h-0 flex-col px-6 pt-4 pb-0')}>
        {tagCategoryBadge(displayTag, t)}
        <p className={cn(cardStyles.title, displayTag ? 'mt-4' : 'mt-0')}>{title}</p>
        {!showCategoryIcon && author && <span className="text-xs text-gray-500">{author}</span>}
        {!showCategoryIcon && description && (
          <span className="text-xs text-gray-500 line-clamp-2">{description}</span>
        )}
      </div>
      <div className="px-6 flex flex-col gap-2">
        {metaRows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex items-center gap-4 text-denim-800 text-sm font-normal leading-4"
          >
            {row.map((item, itemIndex) => {
              const Icon = item.icon;
              return (
                <div key={itemIndex} className="flex items-center gap-2">
                  <Icon className={cardStyles.metaGlyph} />
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="px-6 mt-auto pt-4 pb-6">
        <Button
          variant="terciary"
          size="lg"
          icon={showTrailingIcon ? 'trailing' : undefined}
          className={cardStyles.cta}
          onClick={handleOnClick}
          disabled={isButtonDisabled}
        >
          {getButtonText()}
          {showTrailingIcon && (
            <ChevronRight className="size-6 shrink-0 text-current" aria-hidden />
          )}
        </Button>
      </div>
    </div>
  );
};
